import { Row, Col } from "antd";
import { AmountsCard } from "./components/AmountsCard";
import { useFetchData } from "./hooks/useFetchData";
import { useAccountsInProgress } from "./hooks/useAccountsInProgress";
import { useGetTotals } from "./hooks/useGetTotals";
import { InternalServerError } from "../InternalServerError";
import CreatedItems from "./components/CreatedItems";
import { useGetCreatedItems } from "./hooks/useGetCreatedItems";
import { AccountsInProgress } from "./components/AccountsInProgress";
import { TypicalAccountSetup } from "./components/TypicalAccountSetup";
import { useGetCustomersData } from "./hooks/useGetCustomersData";
import { PieChartContainer } from "./components/PieChartContainer";
import { Header } from "./components/Header";
import { useRecalculate } from "./hooks/useRecalculate";
import AccountsWithPR from "./components/AccountsWithPR";
import { useAmountsWidget } from "./hooks/useAmountsWidget";
import ImpoundBalance from "./components/ImpoundBalance";
import { PayoffAmounts } from "./components/PayoffAmounts";
import { usePayoffAmounts } from "./hooks/usePayoffAmounts";
import { TransactionByMethod } from "./components/TransactionByMethod";
import Correspondence from "./components/Correspondence";
import { useGetCorrespondenceStats } from "./hooks/useGetCorrespondenceStats";
import { ChatConversations } from "./components/ChatConversations";
import { useChatConversations } from "./hooks/useChatConversation";
import PortalVisits from "./components/PortalVisits";
import { usePortalVisits } from "./hooks/usePortalVisits";
import VisitsPerDevice from "./components/VisitsPerDevice";
import TransactionAmounts from "./components/TransactionAmounts";
import { useTransactionAmounts } from "./hooks/useTransactionAmounts";
import "./styles.scss";

export const Dashboard = () => {
  const { isRecalculated, recalculate, disabled } = useRecalculate();

  const { opened, closed, other, draft, isLoading, data, noKpi } =
    useFetchData(isRecalculated);

  const { customersData } = useGetCustomersData(data);

  const { createdItems } = useGetCreatedItems(data);

  const { totalImpound, closedImpounds } = useGetTotals(data);

  const { incidentOptions, incidentAmounts } = useAmountsWidget(data);

  const { data: accountsInProgress } = useAccountsInProgress(draft, other);

  const { payoffAmounts } = usePayoffAmounts(data);

  const { correspondenceStats } = useGetCorrespondenceStats(data);

  const { conversations } = useChatConversations(data);

  const { visits } = usePortalVisits(data);

  const { amounts } = useTransactionAmounts(data);

  return (
    <Row className="table-page-content">
      <Row className="d-block w-100 h-100">
        <Header data={data} disabled={disabled} recalculate={recalculate} />
        <Row className="home-content d-block overflow-y-auto">
          <Row className="h-100 mx-auto pt-8 d-block">
            {!noKpi ? (
              <>
                <Row className="w-100">
                  <Col span={16} className="pr-4">
                    <CreatedItems
                      data={createdItems}
                      isLoading={isLoading}
                      isRecalculated={isRecalculated}
                    />
                  </Col>
                  <Col span={8}>
                    <AccountsInProgress
                      data={accountsInProgress}
                      isLoading={isLoading}
                    />

                    <AmountsCard
                      title="Incidents Number"
                      isLoading={isLoading}
                      options={incidentOptions}
                      paymnentAmounts={incidentAmounts}
                    />
                  </Col>
                </Row>
                <Row className="w-100 mt-4">
                  <Col span={8} className="pr-4">
                    <PieChartContainer
                      header="Open Accounts"
                      isLoading={isLoading}
                      data={opened?.values}
                      colors={opened?.colors}
                      source={1}
                      showTooltip
                    />
                  </Col>
                  <Col span={8} className="pr-4">
                    <PieChartContainer
                      header="Closed Accounts"
                      isLoading={isLoading}
                      data={closed?.values}
                      colors={closed?.colors}
                      source={1}
                    />
                  </Col>
                  <Col span={8}>
                    <AccountsWithPR data={data} isLoading={isLoading} />
                  </Col>
                </Row>
                <Row className="mt-4 w-100">
                  <Col span={8} className="pr-4">
                    <PieChartContainer
                      header="Customers"
                      isLoading={isLoading}
                      data={customersData?.values}
                      colors={customersData?.colors}
                      source={2}
                    />
                  </Col>
                  <Col span={8} className="pr-4">
                    <TypicalAccountSetup data={data} isLoading={isLoading} />
                  </Col>
                  <Col span={8}>
                    <ImpoundBalance
                      totalImpound={totalImpound}
                      closedImpounds={closedImpounds}
                      isDataLoading={isLoading}
                      isRecalculated={isRecalculated}
                    />
                  </Col>
                </Row>
                <Row className="mt-4 w-100">
                  <Col span={16} className="pr-4">
                    <TransactionAmounts
                      amounts={amounts}
                      isDataLoading={isLoading}
                      isRecalculated={isRecalculated}
                    />
                  </Col>
                  <Col span={8}>
                    <PayoffAmounts
                      isDataLoading={isLoading}
                      payoffAmounts={payoffAmounts}
                      isRecalculated={isRecalculated}
                    />
                  </Col>
                </Row>
                <Row className="mt-4 w-100">
                  <Col span={16} className="pr-4">
                    <TransactionByMethod isRecalculated={isRecalculated} />
                  </Col>
                  <Col span={8}>
                    <Correspondence
                      data={correspondenceStats}
                      isDataLoading={isLoading}
                      isRecalculated={isRecalculated}
                    />
                  </Col>
                </Row>
                <Row className="mt-4 w-100 pb-9">
                  <Col span={8} className="pr-4">
                    <PortalVisits
                      visits={visits}
                      isDataLoading={isLoading}
                      isRecalculated={isRecalculated}
                    />
                  </Col>
                  <Col span={8} className="pr-4">
                    <VisitsPerDevice
                      isDataLoading={isLoading}
                      isRecalculated={isRecalculated}
                    />
                  </Col>
                  <Col span={8}>
                    <ChatConversations
                      conversations={conversations}
                      isDataLoading={isLoading}
                      isRecalculated={isRecalculated}
                    />
                  </Col>
                </Row>
              </>
            ) : (
              <InternalServerError dashboard />
            )}
          </Row>
        </Row>
      </Row>
    </Row>
  );
};
